import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  CogIcon,
  UserIcon,
  KeyIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  UserPlusIcon,
  TrophyIcon,
  ShieldCheckIcon,
  EnvelopeIcon,
  HeartIcon,
  VideoCameraIcon,
  PlusIcon,
  TrashIcon,
  CalendarDaysIcon,
  ArrowTopRightOnSquareIcon,
  QuestionMarkCircleIcon,
  AcademicCapIcon,
} from '@heroicons/react/24/outline';
import {
  getGlobalSettings,
  updateSettings
} from '../services/settingsService';
import {
  createUser,
  migrateSequenceNumbers,
  getClassSettings,
  updateClassSettings,
  getInstructors,
  setLiveBroadcastState
} from '../services/firebaseService';
import { User, AppSettings, ClassSettings, ClassScheduleEntry } from '../types';
import { getEmailServiceStatus } from '../services/emailService';
import { extractYouTubeVideoId, formatYouTubeThumbnailUrl } from '../utils/helpers';
import CertificateTemplateEditor from '../components/admin/CertificateTemplateEditor';
import EmailTemplateEditor from '../components/admin/EmailTemplateEditor';
import UserAttendanceAnalytics from '../components/admin/UserAttendanceAnalytics';
import WeeklyQueriesAdminReport from '../components/admin/WeeklyQueriesAdminReport';
import AdminExamManagement from '../components/admin/AdminExamManagement';
import AdminHeader from '../components/admin/AdminHeader';

export interface AdminSettingsPageProps {
  defaultTab?: string;
}

const AdminSettingsPage: React.FC<AdminSettingsPageProps> = ({ defaultTab }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<string>(urlTab || defaultTab || 'profile');
  const [adminUser, setAdminUser] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const canManageRoles = adminUser?.role === 'super_admin';
  const navigate = useNavigate();

  useEffect(() => {
    if (urlTab && urlTab !== activeTab) {
      setActiveTab(urlTab);
    }
  }, [urlTab, activeTab]);

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('tab', tabId);
      return next;
    });
  };

  // Form states
  const [profileForm, setProfileForm] = useState({
    name: '',
    email: '',
    role: 'admin'
  });

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [systemSettings, setSystemSettings] = useState<AppSettings>({
    misCoordinatorLabel: 'MIS Coordinator',
    emailNotificationsEnabled: true,
    senderEmail: 'kreedodisha@gmail.com'
  });

  const [createUserForm, setCreateUserForm] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'admin'
  });

  const [creatingUser, setCreatingUser] = useState(false);

  const [emailServiceStatus, setEmailServiceStatus] = useState<any>({
    configured: false,
    enabled: false,
    serviceId: '',
    templatesConfigured: 0,
    usage: { dailyCount: 0, monthlyCount: 0, limit: 450, warning: 300 }
  });

  // Class & Schedule Settings State
  const [classSettings, setClassSettings] = useState<ClassSettings>({
    youtubeUrl: '',
    isLiveNow: false,
    liveLabel: 'Second Phase Online Training — Live Session',
    scheduleEntries: [],
    updatedAt: new Date(),
  });
  const [instructorsList, setInstructorsList] = useState<User[]>([]);
  const [loadingInstructors, setLoadingInstructors] = useState(false);
  const [selectedLiveInstructorId, setSelectedLiveInstructorId] = useState<string>('');
  const [customYouTubeUrl, setCustomYouTubeUrl] = useState<string>('');
  const [savingClassSettings, setSavingClassSettings] = useState(false);
  const [newScheduleEntry, setNewScheduleEntry] = useState({
    title: '',
    batchLabel: '',
    scheduledAt: '',
    durationMinutes: 60,
    topic: '',
    instructorId: '',
    instructorName: '',
  });


  useEffect(() => {
    const user = localStorage.getItem('adminUser');
    if (!user) {
      navigate('/admin/login');
      return;
    }

    const userData = JSON.parse(user);
    setAdminUser(userData);
    setProfileForm({
      name: userData.name || '',
      email: userData.email || '',
      role: userData.role || 'admin'
    });

    // Load system settings from Firestore
    const fetchSettings = async () => {
      const settings = await getGlobalSettings();
      setSystemSettings(settings);
    };
    fetchSettings();

    const fetchStats = async () => {
      const status = await getEmailServiceStatus();
      setEmailServiceStatus(status);
    };
    fetchStats();

    // Fetch class settings & instructors
    const fetchClassAndInstructors = async () => {
      try {
        const cs = await getClassSettings();
        if (cs) {
          setClassSettings(cs);
          if (cs.activeInstructorId) {
            setSelectedLiveInstructorId(cs.activeInstructorId);
          }
        }
      } catch (e) {
        console.error('Error fetching class settings:', e);
      }

      setLoadingInstructors(true);
      try {
        const insts = await getInstructors();
        setInstructorsList(insts);
        const gauri = insts.find(i => i.displayName?.toLowerCase().includes('gauri') || i.email?.includes('gauri')) || insts[0];
        if (gauri) {
          setNewScheduleEntry(prev => ({
            ...prev,
            instructorId: prev.instructorId || gauri.uid,
            instructorName: prev.instructorName || gauri.displayName || 'Gauri Tiwari Maam',
          }));
        }
      } catch (e) {
        console.error('Error fetching instructors:', e);
      } finally {
        setLoadingInstructors(false);
      }
    };
    fetchClassAndInstructors();
  }, [navigate]);


  const handleSaveClassSettings = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setSavingClassSettings(true);
    try {
      await updateClassSettings(classSettings);
      alert('Class schedule & instructor assignments published successfully!');
    } catch (err: any) {
      console.error('Error saving class settings:', err);
      alert('Failed to save class settings: ' + (err.message || 'Unknown error'));
    } finally {
      setSavingClassSettings(false);
    }
  };

  const handleToggleLive = async () => {
    const nextLive = !classSettings.isLiveNow;
    let streamUrl = classSettings.youtubeUrl || '';
    let liveLabel = classSettings.liveLabel || 'Online Lecture Session';
    let instructorId = classSettings.activeInstructorId || '';
    let instructorName = classSettings.activeInstructorName || '';

    // If going live, determine which stream to activate (custom override OR instructor stream)
    if (nextLive) {
      if (customYouTubeUrl.trim()) {
        streamUrl = customYouTubeUrl.trim();
        liveLabel = 'Live Lecture (Direct Admin Broadcast)';
        instructorId = adminUser?.uid || 'admin';
        instructorName = adminUser?.name || 'Central Administration';
      } else if (selectedLiveInstructorId && classSettings.instructors && classSettings.instructors[selectedLiveInstructorId]) {
        const inst = classSettings.instructors[selectedLiveInstructorId];
        streamUrl = inst.streamUrl;
        liveLabel = inst.streamTitle || `Live Lecture by ${inst.instructorName}`;
        instructorId = inst.instructorId;
        instructorName = inst.instructorName;
      } else if (classSettings.instructors) {
        // Pick first instructor with a stream URL
        const available = Object.values(classSettings.instructors).find(i => Boolean(i.streamUrl));
        if (available) {
          streamUrl = available.streamUrl;
          liveLabel = available.streamTitle || `Live Lecture by ${available.instructorName}`;
          instructorId = available.instructorId;
          instructorName = available.instructorName;
        }
      }

      if (!streamUrl) {
        alert('Cannot start broadcast: Please enter a YouTube URL or select an instructor with an uploaded stream link.');
        return;
      }
    }

    try {
      await setLiveBroadcastState({
        isLiveNow: nextLive,
        youtubeUrl: streamUrl,
        liveLabel,
        instructorId,
        instructorName
      });
      setClassSettings(prev => ({
        ...prev,
        isLiveNow: nextLive,
        youtubeUrl: streamUrl,
        liveLabel,
        activeInstructorId: instructorId,
        activeInstructorName: instructorName
      }));
    } catch (err: any) {
      console.error('Error toggling live state:', err);
      alert('Failed to toggle live state: ' + err.message);
    }
  };

  const handleAddScheduleEntry = () => {
    if (!newScheduleEntry.title.trim()) {
      alert('Please provide a class session title.');
      return;
    }
    const gauriDefault = instructorsList.find(i => i.displayName?.toLowerCase().includes('gauri') || i.email?.includes('gauri')) || instructorsList[0];
    const assigned = instructorsList.find(i => i.uid === newScheduleEntry.instructorId) || gauriDefault;
    const entry: ClassScheduleEntry = {
      id: 'entry_' + Date.now(),
      title: newScheduleEntry.title.trim(),
      batchLabel: newScheduleEntry.batchLabel.trim(),
      scheduledAt: newScheduleEntry.scheduledAt ? new Date(newScheduleEntry.scheduledAt) : new Date(),
      durationMinutes: Number(newScheduleEntry.durationMinutes) || 60,
      topic: newScheduleEntry.topic.trim(),
      instructorId: assigned?.uid || newScheduleEntry.instructorId || 'pjiA5PN3rfM2FtafHFyrxcPgk6j1',
      instructorName: assigned?.displayName || assigned?.email || 'Gauri Tiwari Maam',
      instructorEmail: assigned?.email || 'gauritiwaribusiness@gmail.com',
      streamUrl: (classSettings.instructors && assigned?.uid && classSettings.instructors[assigned.uid]?.streamUrl) || '',
    };
    const updatedEntries = [...(classSettings.scheduleEntries || []), entry];
    setClassSettings({ ...classSettings, scheduleEntries: updatedEntries });
    setNewScheduleEntry({
      title: '',
      batchLabel: '',
      scheduledAt: '',
      durationMinutes: 60,
      topic: '',
      instructorId: '',
      instructorName: '',
    });
  };

  const handleDeleteScheduleEntry = (id: string) => {
    const updatedEntries = (classSettings.scheduleEntries || []).filter(e => e.id !== id);
    setClassSettings({ ...classSettings, scheduleEntries: updatedEntries });
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { role, ...profileData } = profileForm;
      const updatedUser = { ...adminUser, ...profileData };
      localStorage.setItem('adminUser', JSON.stringify(updatedUser));
      setAdminUser(updatedUser);

      alert('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Error updating profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      alert('New password and confirm password do not match.');
      setLoading(false);
      return;
    }

    try {
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

      alert('Password changed successfully!');
    } catch (error) {
      console.error('Error changing password:', error);
      alert('Error changing password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSystemSettingsUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await updateSettings(systemSettings);
      setEmailServiceStatus(getEmailServiceStatus());
      alert('System settings updated successfully and synced to cloud!');
    } catch (error) {
      console.error('Error updating system settings:', error);
      alert('Error updating system settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'profile', name: 'Profile', icon: UserIcon },
    { id: 'password', name: 'Password', icon: KeyIcon },
    { id: 'system', name: 'System', icon: CogIcon },
    { id: 'class-stream', name: 'Class Schedules & Instructors', icon: CalendarDaysIcon },
    { id: 'class-attendance', name: 'Video Classes, Attendance & Chats', icon: VideoCameraIcon, badge: 'Segmented' },
    { id: 'weekly-queries', name: 'Weekly Student Queries', icon: QuestionMarkCircleIcon, badge: 'Weekly' },
    { id: 'exams-schedule', name: 'Phase 2 Exams & Mock Tests', icon: AcademicCapIcon, badge: 'Live' },
    { id: 'email-templates', name: 'Email Templates', icon: EnvelopeIcon },
    { id: 'certificates', name: 'Certificates', icon: TrophyIcon },
    { id: 'users', name: 'User Management', icon: UserPlusIcon }
  ];

  const isWideTab = activeTab === 'class-attendance' || activeTab === 'weekly-queries' || activeTab === 'exams-schedule';

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Unified Admin Navigation Header */}
      <AdminHeader
        activeNav="settings"
        title="Admin Command Center"
        subtitle="Portal Settings, Videos, Queries & Access"
        isLiveNow={classSettings?.isLiveNow}
      />

      <div className={`${isWideTab ? 'max-w-[100rem]' : 'max-w-7xl'} w-full mx-auto py-5 sm:py-8 px-3 sm:px-6 lg:px-8 pb-24 lg:pb-12 transition-all`}>

        {/* Mobile Tab Switcher (Visible ONLY on mobile & tablet < lg) */}
        <div className="lg:hidden space-y-2 mb-4">
          <div className="bg-white rounded-2xl p-2 border border-slate-200/80 shadow-sm flex items-center overflow-x-auto no-scrollbar scroll-smooth space-x-1.5">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isSelected = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex-shrink-0 flex items-center space-x-1.5 px-3 py-2 rounded-xl text-xs font-bold transition ${isSelected
                      ? tab.id === 'class-attendance'
                        ? 'bg-rose-600 text-white shadow-sm'
                        : tab.id === 'weekly-queries'
                          ? 'bg-indigo-600 text-white shadow-sm'
                          : tab.id === 'exams-schedule'
                            ? 'bg-amber-600 text-white shadow-sm'
                            : 'bg-primary-600 text-white shadow-sm'
                      : 'bg-slate-50 text-slate-700 hover:bg-slate-100'
                    }`}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <span>{tab.name}</span>
                  {tab.badge && (
                    <span className={`text-[9px] font-black px-1.5 py-0.2 rounded-full ${isSelected ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-800'
                      }`}>
                      {tab.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Quick Dropdown Selector on Mobile */}
          <div className="bg-white rounded-2xl p-3 border border-slate-200/80 shadow-sm flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
              Active Section:
            </span>
            <select
              value={activeTab}
              onChange={(e) => handleTabChange(e.target.value)}
              className="text-xs font-bold text-slate-900 bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 focus:ring-2 focus:ring-primary-500"
            >
              {tabs.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} {t.badge ? `(${t.badge})` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className={`grid grid-cols-1 ${isWideTab ? 'xl:grid-cols-5' : 'lg:grid-cols-4'} gap-6`}>
          {/* Desktop Sticky Sidebar (Hidden on mobile to eliminate massive scrolling block) */}
          <div className={`hidden lg:block ${isWideTab ? 'xl:col-span-1' : 'lg:col-span-1'}`}>
            <div className="bg-white rounded-3xl shadow-sm border border-slate-200/80 p-3.5 sticky top-24">
              <div className="px-3 py-2 mb-1 border-b border-slate-100">
                <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400">Settings Navigation</p>
              </div>
              <nav className="space-y-1 pt-1">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isSelected = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => handleTabChange(tab.id)}
                      className={`w-full flex items-center justify-between px-3 py-2.5 rounded-2xl text-left transition-all ${isSelected
                          ? tab.id === 'class-attendance'
                            ? 'bg-rose-50 text-rose-800 border border-rose-200 font-bold shadow-sm'
                            : tab.id === 'weekly-queries'
                              ? 'bg-indigo-50 text-indigo-800 border border-indigo-200 font-bold shadow-sm'
                              : tab.id === 'exams-schedule'
                                ? 'bg-amber-50 text-amber-800 border border-amber-200 font-bold shadow-sm'
                                : 'bg-primary-50 text-primary-700 border border-primary-200 font-bold shadow-sm'
                          : 'text-slate-700 hover:bg-slate-50 font-medium'
                        }`}
                    >
                      <div className="flex items-center space-x-2.5 min-w-0">
                        <Icon className={`w-4 h-4 flex-shrink-0 ${isSelected
                            ? (tab.id === 'class-attendance' ? 'text-rose-600' : tab.id === 'weekly-queries' ? 'text-indigo-600' : tab.id === 'exams-schedule' ? 'text-amber-600' : 'text-primary-600')
                            : 'text-slate-400'
                          }`} />
                        <span className="text-xs truncate">{tab.name}</span>
                      </div>
                      {tab.badge && (
                        <span className={`text-[9px] font-bold px-1.5 py-0.2 rounded-full uppercase tracking-wider ml-1 flex-shrink-0 ${tab.id === 'weekly-queries'
                            ? 'bg-indigo-100 text-indigo-700'
                            : tab.id === 'exams-schedule'
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-rose-100 text-rose-700'
                          }`}>
                          {tab.badge}
                        </span>
                      )}
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Main Content Area */}
          <div className={isWideTab ? 'xl:col-span-4' : 'lg:col-span-3'}>
            <div className={`bg-white rounded-3xl shadow-sm border border-slate-200/80 ${isWideTab ? 'p-3 sm:p-5' : 'p-5 sm:p-7'}`}>
              {/* Profile Tab */}
              {activeTab === 'profile' && (
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-6">Profile Information</h2>

                  <form onSubmit={handleProfileUpdate} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Full Name
                        </label>
                        <input
                          type="text"
                          value={profileForm.name}
                          onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          placeholder="Enter your full name"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email Address
                        </label>
                        <input
                          type="email"
                          value={profileForm.email}
                          onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          placeholder="Enter your email"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Role
                        </label>
                        <select
                          value={profileForm.role}
                          disabled={true}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed focus:ring-0"
                        >
                          <option value="super_admin">Super Admin</option>
                          <option value="admin">Admin</option>
                          <option value="accounts">Accounts</option>
                          <option value="deo">DEO</option>
                          <option value="instructor">Instructor</option>
                        </select>
                        <p className="mt-1 text-xs text-gray-500">Roles can only be changed by a Super Admin.</p>
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <button
                        type="submit"
                        disabled={loading}
                        className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {loading ? 'Updating...' : 'Update Profile'}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Password Tab */}
              {activeTab === 'password' && (
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-6">Change Password</h2>

                  <form onSubmit={handlePasswordChange} className="space-y-6">
                    <div className="max-w-md space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Current Password
                        </label>
                        <input
                          type="password"
                          value={passwordForm.currentPassword}
                          onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          placeholder="Enter current password"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          New Password
                        </label>
                        <input
                          type="password"
                          value={passwordForm.newPassword}
                          onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          placeholder="Enter new password"
                          required
                          minLength={6}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Confirm New Password
                        </label>
                        <input
                          type="password"
                          value={passwordForm.confirmPassword}
                          onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          placeholder="Confirm new password"
                          required
                          minLength={6}
                        />
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <button
                        type="submit"
                        disabled={loading}
                        className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {loading ? 'Updating...' : 'Change Password'}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* System Tab */}
              {activeTab === 'system' && (
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-6">System Settings</h2>

                  <form onSubmit={handleSystemSettingsUpdate} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          MIS Coordinator Label
                        </label>
                        <input
                          type="text"
                          value={systemSettings.misCoordinatorLabel}
                          onChange={(e) => setSystemSettings({ ...systemSettings, misCoordinatorLabel: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          placeholder="e.g., MIS Coordinator, Regional Coordinator"
                        />
                        <p className="text-sm text-gray-500 mt-1">This label will be used throughout the platform for this role.</p>
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Official Sender Email
                        </label>
                        <input
                          type="email"
                          value={systemSettings.senderEmail}
                          onChange={(e) => setSystemSettings({ ...systemSettings, senderEmail: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          placeholder="kreedodisha@gmail.com"
                        />
                        <p className="text-sm text-gray-500 mt-1">Used as the 'From' address for all automated emails.</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h3 className="text-md font-medium text-gray-900">Email Notifications</h3>

                      {/* Email Service Status */}
                      <div className="bg-gray-50 rounded-lg p-4 mb-4">
                        <h4 className="text-sm font-medium text-gray-900 mb-3">Email Service Status</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div className="flex items-center space-x-2">
                            <CheckCircleIcon className="w-4 h-4 text-green-500" />
                            <span className="text-gray-700">
                              System: {emailServiceStatus.serviceId}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <CheckCircleIcon className="w-4 h-4 text-green-500" />
                            <span className="text-gray-700">
                              Status: {systemSettings.emailNotificationsEnabled ? 'Active' : 'Paused'}
                            </span>
                          </div>
                        </div>

                        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-sm text-blue-800">
                            <strong>Nodemailer Backend:</strong> The system is now using a server-side SMTP relay for improved reliability and PDF attachment support.
                          </p>
                        </div>
                      </div>

                      {/* Email Usage Tracking */}
                      <div className="bg-gray-50 rounded-lg p-4 mb-4">
                        <h4 className="text-sm font-medium text-gray-900 mb-3">Email Usage Tracking</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">Daily Emails (24h)</span>
                              <span className={`font-bold ${emailServiceStatus.usage?.dailyCount >= emailServiceStatus.usage?.limit ? 'text-red-600' : emailServiceStatus.usage?.dailyCount >= emailServiceStatus.usage?.warning ? 'text-orange-600' : 'text-green-600'}`}>
                                {emailServiceStatus.usage?.dailyCount || 0} / {emailServiceStatus.usage?.limit || 450}
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                              <div
                                className={`h-2.5 rounded-full transition-all duration-500 ${emailServiceStatus.usage?.dailyCount >= emailServiceStatus.usage?.limit ? 'bg-red-500' : emailServiceStatus.usage?.dailyCount >= emailServiceStatus.usage?.warning ? 'bg-orange-500' : 'bg-green-500'}`}
                                style={{ width: `${Math.min(100, ((emailServiceStatus.usage?.dailyCount || 0) / (emailServiceStatus.usage?.limit || 450)) * 100)}%` }}
                              ></div>
                            </div>
                            {emailServiceStatus.usage?.dailyCount >= emailServiceStatus.usage?.warning && (
                              <p className={`text-xs flex items-center mt-1 ${emailServiceStatus.usage?.dailyCount >= emailServiceStatus.usage?.limit ? 'text-red-600 font-semibold' : 'text-orange-600'}`}>
                                <ExclamationTriangleIcon className="w-3.5 h-3.5 mr-1" />
                                {emailServiceStatus.usage?.dailyCount >= emailServiceStatus.usage?.limit ? 'Daily limit reached! Further emails will be blocked.' : 'Warning: Approaching daily delivery limit.'}
                              </p>
                            )}
                          </div>

                          <div className="flex flex-col justify-center bg-white p-3 rounded-md border border-gray-100 shadow-sm">
                            <div className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-1">Monthly Volume</div>
                            <div className="text-2xl font-bold text-gray-900">{emailServiceStatus.usage?.monthlyCount || 0}</div>
                            <div className="text-[10px] text-gray-400 mt-1">Total emails successfully sent this month</div>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <label className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            checked={systemSettings.emailNotificationsEnabled}
                            onChange={(e) => setSystemSettings({ ...systemSettings, emailNotificationsEnabled: e.target.checked })}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          />
                          <div className="flex-1">
                            <span className="text-sm text-gray-700">Send email notifications to applicants</span>
                            <p className="text-xs text-gray-500">
                              Automatically send confirmation and status update emails with attachments.
                            </p>
                          </div>
                        </label>
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <button
                        type="submit"
                        disabled={loading}
                        className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {loading ? 'Updating...' : 'Save Settings'}
                      </button>
                    </div>
                  </form>

                  {/* System Maintenance */}
                  <div className="mt-12 pt-8 border-t border-gray-200">
                    <h3 className="text-md font-medium text-gray-900 mb-4">System Maintenance</h3>
                    <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">Migrate Sequence Numbers</h4>
                        <p className="text-xs text-gray-500 mt-1 max-w-md">
                          Run this utility if you are upgrading from an older version. It will scan all existing applications and assign a sequence number to them so that Special IDs remain synced correctly across months.
                        </p>
                      </div>
                      <button
                        onClick={async () => {
                          if (!window.confirm("Are you sure you want to run the sequence number migration on all applications?")) return;
                          setLoading(true);
                          try {
                            const updatedCount = await migrateSequenceNumbers();
                            alert(`Migration successful! Updated ${updatedCount} existing records.`);
                          } catch (err: any) {
                            alert('Migration failed: ' + err.message);
                          } finally {
                            setLoading(false);
                          }
                        }}
                        disabled={loading}
                        className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-lg font-medium text-sm disabled:opacity-50 transition-colors whitespace-nowrap ml-4 shadow-sm"
                      >
                        Run Migration
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Class Schedules & Instructors Tab */}
              {activeTab === 'class-stream' && (
                <div>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                    <div>
                      <div className="flex items-center space-x-2">
                        <h2 className="text-lg font-semibold text-gray-900">Class Schedules & Assigned Instructors</h2>
                        <span className="bg-primary-50 text-primary-700 text-xs px-2.5 py-0.5 rounded-full font-semibold border border-primary-200">
                          okreed.in/instructor
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        Schedule class slots and assign instructors. Live stream links are uploaded by instructors in the Instructor Portal.
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={() => navigate('/instructor')}
                        className="inline-flex items-center space-x-1.5 px-3.5 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg text-sm font-medium border border-indigo-200 transition-colors"
                      >
                        <span>Open Instructor Portal</span>
                        <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Notice Box: Stream Links Managed on /instructor */}
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 text-sm text-blue-900 flex items-start space-x-3">
                    <VideoCameraIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-semibold text-blue-950">Stream Links Moved to Instructor Portal</p>
                      <p className="text-xs text-blue-800 mt-0.5">
                        YouTube stream URLs and live preview testing are now handled directly by instructors at <span className="font-mono font-semibold">/instructor</span>. As Admin, you manage lecture schedules, assign instructors, and have full control to start or stop live broadcasts at any time.
                      </p>
                    </div>
                  </div>

                  {/* Quick Status / Admin Go Live Control Card */}
                  <div className={`p-5 rounded-xl border mb-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 ${classSettings.isLiveNow
                      ? 'bg-red-50 border-red-200'
                      : 'bg-gray-50 border-gray-200'
                    }`}>
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className={`inline-block w-3 h-3 rounded-full ${classSettings.isLiveNow ? 'bg-red-500 animate-ping' : 'bg-gray-400'
                          }`} />
                        <h3 className="font-semibold text-gray-900">
                          Broadcast Status: {classSettings.isLiveNow ? '🔴 LIVE NOW ON STUDENT PORTAL' : '⚪ OFFLINE'}
                        </h3>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">
                        {classSettings.isLiveNow ? (
                          <span>
                            Streaming: <strong className="text-gray-900">{classSettings.liveLabel}</strong>
                            {classSettings.activeInstructorName && (
                              <span> &bull; Instructor: <strong>{classSettings.activeInstructorName}</strong></span>
                            )}
                          </span>
                        ) : (
                          'Students see the offline placeholder and schedule timetable until a broadcast goes live.'
                        )}
                      </p>

                      {!classSettings.isLiveNow && (
                        <div className="mt-3 space-y-2">
                          <label className="text-xs font-bold text-gray-700 block">Select Broadcast Stream Source:</label>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <div>
                              <label className="text-[11px] font-semibold text-gray-600 block mb-1">Instructor Uploaded Stream</label>
                              <select
                                value={selectedLiveInstructorId}
                                onChange={(e) => {
                                  setSelectedLiveInstructorId(e.target.value);
                                  if (e.target.value) setCustomYouTubeUrl('');
                                }}
                                className="w-full text-xs px-2.5 py-1.5 bg-white border border-gray-300 rounded-lg focus:ring-1 focus:ring-primary-500"
                              >
                                <option value="">Auto-select (First Available Stream)</option>
                                {instructorsList.map((inst) => {
                                  const stream = classSettings.instructors?.[inst.uid];
                                  const hasUrl = Boolean(stream?.streamUrl);
                                  return (
                                    <option key={inst.uid} value={inst.uid}>
                                      {inst.displayName || inst.email} {hasUrl ? '🟢 (Stream Ready)' : '⚪ (No URL)'}
                                    </option>
                                  );
                                })}
                              </select>
                            </div>

                            <div>
                              <label className="text-[11px] font-semibold text-gray-600 block mb-1">Direct YouTube Link Override</label>
                              <input
                                type="text"
                                value={customYouTubeUrl}
                                onChange={(e) => {
                                  setCustomYouTubeUrl(e.target.value);
                                  if (e.target.value) setSelectedLiveInstructorId('');
                                }}
                                placeholder="https://www.youtube.com/watch?v=..."
                                className="w-full text-xs px-2.5 py-1.5 bg-white border border-gray-300 rounded-lg focus:ring-1 focus:ring-primary-500 font-mono"
                              />
                            </div>
                          </div>

                          {customYouTubeUrl && extractYouTubeVideoId(customYouTubeUrl) && (
                            <div className="flex items-center space-x-3 p-2 bg-white rounded-xl border border-rose-200 mt-2">
                              <img
                                src={formatYouTubeThumbnailUrl(extractYouTubeVideoId(customYouTubeUrl)!, 'default')}
                                alt="Preview"
                                className="w-16 h-10 object-cover rounded-lg shadow-xs"
                              />
                              <div className="text-xs">
                                <p className="font-bold text-gray-900 flex items-center space-x-1.5">
                                  <span className="text-rose-600">📹</span>
                                  <span>YouTube ID: <strong className="font-mono text-rose-700">{extractYouTubeVideoId(customYouTubeUrl)}</strong></span>
                                </p>
                                <p className="text-[11px] text-gray-500">Attendance, chat messages, and view log trail will segment under this video link.</p>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center space-x-3 w-full md:w-auto justify-end">
                      <button
                        type="button"
                        onClick={handleToggleLive}
                        className={`px-5 py-2.5 rounded-lg font-semibold text-sm text-white transition-all shadow-sm ${classSettings.isLiveNow
                            ? 'bg-red-600 hover:bg-red-700'
                            : 'bg-emerald-600 hover:bg-emerald-700'
                          }`}
                      >
                        {classSettings.isLiveNow ? 'End Live Broadcast' : 'Go Live Now (Admin Override)'}
                      </button>
                    </div>
                  </div>

                  {/* Class Schedule Manager */}
                  <div className="pt-2 border-t border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-md font-semibold text-gray-900">Manage Upcoming Class Schedule</h3>
                        <p className="text-xs text-gray-500">
                          Create scheduled lecture slots and assign instructors.
                        </p>
                      </div>
                    </div>

                    {/* Add Schedule Form */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
                      <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-3">Add New Session Slot</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Title *</label>
                          <input
                            type="text"
                            value={newScheduleEntry.title}
                            onChange={(e) => setNewScheduleEntry({ ...newScheduleEntry, title: e.target.value })}
                            placeholder="e.g. Session 1: Sports Nutrition & Fitness"
                            className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-primary-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Batch / Group</label>
                          <input
                            type="text"
                            value={newScheduleEntry.batchLabel}
                            onChange={(e) => setNewScheduleEntry({ ...newScheduleEntry, batchLabel: e.target.value })}
                            placeholder="e.g. All Candidates / Batch A"
                            className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-primary-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Assigned Instructor *</label>
                          <select
                            value={newScheduleEntry.instructorId}
                            onChange={(e) => {
                              const inst = instructorsList.find(i => i.uid === e.target.value);
                              setNewScheduleEntry({
                                ...newScheduleEntry,
                                instructorId: e.target.value,
                                instructorName: inst?.displayName || inst?.email || ''
                              });
                            }}
                            className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-primary-500 bg-white"
                          >
                            <option value="">-- Select Instructor --</option>
                            {instructorsList.map((inst) => {
                              const stream = classSettings.instructors?.[inst.uid];
                              const hasUrl = Boolean(stream?.streamUrl);
                              return (
                                <option key={inst.uid} value={inst.uid}>
                                  {inst.displayName || inst.email} {hasUrl ? '🟢 (Stream Ready)' : '⚪ (No Stream URL)'}
                                </option>
                              );
                            })}
                          </select>
                          {instructorsList.length === 0 && !loadingInstructors && (
                            <p className="text-[11px] text-amber-600 mt-1">
                              No instructors created yet. Create one in the User Management tab.
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Date & Time *</label>
                          <input
                            type="datetime-local"
                            value={newScheduleEntry.scheduledAt}
                            onChange={(e) => setNewScheduleEntry({ ...newScheduleEntry, scheduledAt: e.target.value })}
                            className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-primary-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Duration (minutes)</label>
                          <input
                            type="number"
                            value={newScheduleEntry.durationMinutes}
                            onChange={(e) => setNewScheduleEntry({ ...newScheduleEntry, durationMinutes: Number(e.target.value) })}
                            className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-primary-500"
                          />
                        </div>
                        <div className="lg:col-span-1">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Topic / Notes</label>
                          <input
                            type="text"
                            value={newScheduleEntry.topic}
                            onChange={(e) => setNewScheduleEntry({ ...newScheduleEntry, topic: e.target.value })}
                            placeholder="e.g. Introduction to rules, conditioning guidelines"
                            className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-primary-500"
                          />
                        </div>
                      </div>

                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={handleAddScheduleEntry}
                          className="inline-flex items-center space-x-1 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 px-3 py-1.5 rounded-md text-sm font-medium shadow-sm transition-colors"
                        >
                          <PlusIcon className="w-4 h-4 text-primary-600" />
                          <span>Add Slot to List</span>
                        </button>
                      </div>
                    </div>

                    {/* Schedule Entries List */}
                    {classSettings.scheduleEntries && classSettings.scheduleEntries.length > 0 ? (
                      <div className="space-y-2 mb-4">
                        {classSettings.scheduleEntries.map((entry) => {
                          const instStream = entry.instructorId ? classSettings.instructors?.[entry.instructorId] : undefined;
                          const hasStreamUrl = Boolean(instStream?.streamUrl);

                          return (
                            <div
                              key={entry.id}
                              className="flex items-center justify-between p-3.5 bg-white border border-gray-200 rounded-lg shadow-sm hover:border-gray-300 transition-colors"
                            >
                              <div className="flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="font-semibold text-gray-900 text-sm">{entry.title}</span>
                                  {entry.batchLabel && (
                                    <span className="bg-blue-50 text-blue-700 text-xs px-2 py-0.5 rounded font-medium border border-blue-100">
                                      {entry.batchLabel}
                                    </span>
                                  )}
                                  {entry.instructorName ? (
                                    <span className="bg-purple-50 text-purple-700 text-xs px-2 py-0.5 rounded font-medium border border-purple-200 flex items-center space-x-1">
                                      <span>👤 {entry.instructorName}</span>
                                      {hasStreamUrl ? (
                                        <span className="text-emerald-600 font-semibold" title="Stream link configured">• Ready</span>
                                      ) : (
                                        <span className="text-amber-600 font-semibold" title="No stream URL uploaded yet">• Needs Stream URL</span>
                                      )}
                                    </span>
                                  ) : (
                                    <span className="bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded font-medium">
                                      No Instructor Assigned
                                    </span>
                                  )}
                                </div>
                                {entry.topic && (
                                  <p className="text-xs text-gray-500 mt-1">Topic: {entry.topic}</p>
                                )}
                                <div className="flex items-center space-x-3 text-xs text-gray-400 mt-1">
                                  <span>
                                    Scheduled: {entry.scheduledAt ? new Date(entry.scheduledAt?.toDate ? entry.scheduledAt.toDate() : entry.scheduledAt).toLocaleString() : 'TBA'}
                                  </span>
                                  <span>&bull;</span>
                                  <span>Duration: {entry.durationMinutes} mins</span>
                                </div>

                                {(() => {
                                  const effectiveUrl = entry.streamUrl || instStream?.streamUrl;
                                  const entryVidId = effectiveUrl ? extractYouTubeVideoId(effectiveUrl) : null;
                                  if (!entryVidId) return null;
                                  return (
                                    <div className="flex items-center space-x-3 mt-2.5 p-2 bg-gray-50 rounded-xl border border-gray-200">
                                      <img
                                        src={formatYouTubeThumbnailUrl(entryVidId, 'default')}
                                        alt={entry.title}
                                        className="w-14 h-9 object-cover rounded-lg border border-gray-300 shadow-2xs"
                                      />
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center space-x-2">
                                          <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200">
                                            📹 {entryVidId}
                                          </span>
                                          <a
                                            href={`https://www.youtube.com/watch?v=${entryVidId}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-[11px] text-gray-500 hover:text-gray-800 underline truncate max-w-[200px]"
                                          >
                                            {effectiveUrl}
                                          </a>
                                        </div>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          navigate(`/admin/attendance?mode=videos&video=${entryVidId}`);
                                        }}
                                        className="px-2.5 py-1 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-xs font-bold shadow-2xs transition flex items-center space-x-1 flex-shrink-0"
                                      >
                                        <span>Inspect Video Room →</span>
                                      </button>
                                    </div>
                                  );
                                })()}
                              </div>

                              <button
                                type="button"
                                onClick={() => handleDeleteScheduleEntry(entry.id)}
                                className="text-red-500 hover:text-red-700 p-1.5 rounded hover:bg-red-50 transition-colors ml-4"
                                title="Delete Slot"
                              >
                                <TrashIcon className="w-5 h-5" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 italic mb-4">
                        No scheduled sessions yet. Add one above.
                      </p>
                    )}

                    {/* Save Schedule Button */}
                    <div className="flex justify-end mb-8">
                      <button
                        type="button"
                        onClick={() => handleSaveClassSettings()}
                        disabled={savingClassSettings}
                        className="bg-primary-600 hover:bg-primary-700 text-white px-5 py-2 rounded-lg font-medium text-sm shadow-sm transition-colors disabled:opacity-50"
                      >
                        {savingClassSettings ? 'Saving...' : 'Publish Schedule to Students'}
                      </button>
                    </div>

                    {/* Registered Instructors & Stream Status Overview */}
                    <div className="pt-6 border-t border-gray-200">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">Registered Instructors & Streams Overview</h4>
                      <p className="text-xs text-gray-500 mb-4">
                        Status of all accounts with the 'instructor' role and their uploaded stream links.
                      </p>

                      {instructorsList.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {instructorsList.map((inst) => {
                            const stream = classSettings.instructors?.[inst.uid];
                            const hasStream = Boolean(stream?.streamUrl);

                            return (
                              <div
                                key={inst.uid}
                                className="p-3 bg-white border border-gray-200 rounded-lg flex items-center justify-between"
                              >
                                <div>
                                  <div className="flex items-center space-x-2">
                                    <span className="font-semibold text-sm text-gray-900">{inst.displayName || 'Instructor'}</span>
                                    <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${hasStream ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-gray-100 text-gray-500'
                                      }`}>
                                      {hasStream ? 'Stream Configured' : 'No Stream Uploaded'}
                                    </span>
                                  </div>
                                  <p className="text-xs text-gray-500 mt-0.5">{inst.email}</p>
                                  {hasStream && stream?.streamTitle && (
                                    <p className="text-xs text-primary-600 mt-0.5 font-medium truncate max-w-xs">
                                      Title: {stream.streamTitle}
                                    </p>
                                  )}
                                </div>
                                <span className={`w-2.5 h-2.5 rounded-full ${hasStream ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="p-4 bg-gray-50 rounded-lg text-center text-xs text-gray-500">
                          No instructor accounts found. Head to the <button onClick={() => setActiveTab('users')} className="text-primary-600 underline font-medium">User Management</button> tab to create one.
                        </div>
                      )}
                    </div>

                    {/* ── Gauri Tiwari Maam – Teacher Schedule ── */}
                    <div className="pt-6 border-t border-gray-200 mt-6">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                        <div>
                          <div className="flex items-center space-x-2">
                            <span className="text-lg">📅</span>
                            <h4 className="text-sm font-bold text-gray-900">Gauri Tiwari Maam – Teacher Schedule</h4>
                            <span className="bg-violet-100 text-violet-700 text-[10px] px-2 py-0.5 rounded-full font-bold border border-violet-200 uppercase tracking-wide">
                              Sep 30 – Oct 15
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mt-0.5 ml-7">
                            Daily 2-hour class · 12:50 PM – 3:00 PM · Mondays off
                          </p>
                        </div>
                        <div className="flex items-center space-x-4 ml-7 sm:ml-0">
                          <div className="text-center">
                            <div className="text-xl font-extrabold text-violet-700">14</div>
                            <div className="text-[10px] text-gray-500 font-medium">Class Days</div>
                          </div>
                          <div className="text-center">
                            <div className="text-xl font-extrabold text-emerald-600">28</div>
                            <div className="text-[10px] text-gray-500 font-medium">Total Hours</div>
                          </div>
                          <div className="text-center">
                            <div className="text-xl font-extrabold text-gray-400">2</div>
                            <div className="text-[10px] text-gray-500 font-medium">Mon Off</div>
                          </div>
                        </div>
                      </div>

                      {/* Legend */}
                      <div className="flex items-center space-x-4 mb-3 ml-0">
                        <div className="flex items-center space-x-1.5 text-[11px] text-gray-500">
                          <span className="w-2.5 h-2.5 rounded-full bg-violet-500 inline-block"></span>
                          Class Day
                        </div>
                        <div className="flex items-center space-x-1.5 text-[11px] text-gray-500">
                          <span className="w-2.5 h-2.5 rounded-full bg-amber-400 inline-block"></span>
                          Today
                        </div>
                        <div className="flex items-center space-x-1.5 text-[11px] text-gray-500">
                          <span className="w-2.5 h-2.5 rounded-full bg-gray-300 inline-block"></span>
                          No Class (Monday)
                        </div>
                      </div>

                      {/* Schedule rows */}
                      <div className="space-y-1.5">
                        {(() => {
                          const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                          const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                          const rows = [];
                          const start = new Date(2026, 8, 30); // Sep 30
                          const end = new Date(2026, 9, 15); // Oct 15
                          const todayD = new Date(); todayD.setHours(0, 0, 0, 0);
                          const d = new Date(start);
                          while (d <= end) {
                            const isMonday = d.getDay() === 1;
                            const isToday = d.getTime() === todayD.getTime();
                            const dayNum = d.getDate();
                            const monthA = MONTHS[d.getMonth()];
                            const dayName = DAYS[d.getDay()];
                            rows.push(
                              <div
                                key={`${monthA}-${dayNum}`}
                                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border text-sm transition-all ${isMonday
                                    ? 'bg-gray-50 border-gray-100 opacity-50'
                                    : isToday
                                      ? 'bg-amber-50 border-amber-200 shadow-sm'
                                      : 'bg-white border-gray-200 hover:bg-violet-50 hover:border-violet-200'
                                  }`}
                              >
                                {/* Left accent bar */}
                                <div className={`w-1 h-8 rounded-full flex-shrink-0 ${isMonday ? 'bg-gray-200' : isToday ? 'bg-amber-400' : 'bg-violet-400'
                                  }`} />

                                {/* Date box */}
                                <div className={`flex-shrink-0 w-10 text-center rounded-lg py-0.5 ${isMonday ? 'bg-gray-100' : isToday ? 'bg-amber-100' : 'bg-violet-50'
                                  }`}>
                                  <div className={`text-base font-extrabold leading-tight ${isMonday ? 'text-gray-400' : isToday ? 'text-amber-700' : 'text-violet-700'
                                    }`}>{String(dayNum).padStart(2, '0')}</div>
                                  <div className="text-[9px] font-semibold text-gray-400 uppercase">{monthA}</div>
                                </div>

                                {/* Day name */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center space-x-2">
                                    <span className={`font-semibold ${isMonday ? 'text-gray-400' : isToday ? 'text-amber-800' : 'text-gray-800'
                                      }`}>{dayName}</span>
                                    {isToday && (
                                      <span className="text-[10px] font-bold bg-amber-400 text-white px-1.5 py-0.5 rounded-full animate-pulse">
                                        TODAY
                                      </span>
                                    )}
                                    {!isMonday && (
                                      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${isToday ? 'bg-amber-100 text-amber-700' : 'bg-violet-100 text-violet-700'
                                        }`}>
                                        {isToday ? 'Class Today' : 'Scheduled'}
                                      </span>
                                    )}
                                  </div>
                                </div>

                                {/* Right: time or no-class */}
                                <div className="flex-shrink-0 text-right">
                                  {isMonday ? (
                                    <span className="text-[11px] font-semibold text-gray-400 bg-gray-100 px-2 py-1 rounded-lg">🚫 No Class</span>
                                  ) : (
                                    <div>
                                      <span className={`text-[11px] font-bold px-2 py-1 rounded-lg ${isToday
                                          ? 'bg-amber-100 text-amber-800 border border-amber-200'
                                          : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                        }`}>
                                        🕐 12:50 – 3:00 PM
                                      </span>
                                      <div className="text-[10px] text-gray-400 mt-0.5 text-right">2 hrs</div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                            d.setDate(d.getDate() + 1);
                          }
                          return rows;
                        })()}
                      </div>
                    </div>
                    {/* ── END: Gauri Tiwari Maam Schedule ── */}

                  </div>
                </div>
              )}

              {/* Class Attendance & Views Tab */}
              {activeTab === 'class-attendance' && (
                <UserAttendanceAnalytics />
              )}

              {/* Weekly Student Queries Tab */}
              {activeTab === 'weekly-queries' && (
                <WeeklyQueriesAdminReport />
              )}

              {/* Phase 2 Exams & Benchmarks Tab */}
              {activeTab === 'exams-schedule' && (
                <div className="space-y-6">
                  <AdminExamManagement />
                </div>
              )}

              {/* Certificates Tab */}
              {activeTab === 'certificates' && (
                <CertificateTemplateEditor />
              )}

              {/* Email Templates Tab */}
              {activeTab === 'email-templates' && (
                <EmailTemplateEditor
                  settings={systemSettings}
                  onUpdate={setSystemSettings}
                />
              )}

              {/* User Management Tab */}
              {activeTab === 'users' && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">Create Admin/User</h2>
                      <p className="text-sm text-gray-600">Provision accounts for team roles. Only central admin can create users.</p>
                    </div>
                    <ShieldCheckIcon className="w-8 h-8 text-primary-600" />
                  </div>

                  {!canManageRoles && (
                    <div className="mb-4 rounded-md bg-yellow-50 border border-yellow-200 p-3 text-sm text-yellow-800 flex items-start space-x-2">
                      <ExclamationTriangleIcon className="w-5 h-5 mt-0.5" />
                      <div>Only the Super Admin can create or assign roles. Contact them for access.</div>
                    </div>
                  )}

                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    if (!canManageRoles) return;
                    if (createUserForm.password !== createUserForm.confirmPassword) {
                      alert('Password and confirm password must match.');
                      return;
                    }
                    setCreatingUser(true);
                    try {
                      await createUser(
                        createUserForm.email,
                        createUserForm.password,
                        createUserForm.name,
                        createUserForm.role as User['role']
                      );
                      setCreateUserForm({ name: '', email: '', password: '', confirmPassword: '', role: 'admin' });
                      alert('User created successfully. Share credentials securely with the user.');
                    } catch (error: any) {
                      console.error('Error creating user:', error);
                      alert(error.message || 'Failed to create user.');
                    } finally {
                      setCreatingUser(false);
                    }
                  }}
                    className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                        <input
                          type="text"
                          value={createUserForm.name}
                          onChange={(e) => setCreateUserForm({ ...createUserForm, name: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          placeholder="Enter full name"
                          required
                          disabled={creatingUser || !canManageRoles}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                        <input
                          type="email"
                          value={createUserForm.email}
                          onChange={(e) => setCreateUserForm({ ...createUserForm, email: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          placeholder="user@example.com"
                          required
                          disabled={creatingUser || !canManageRoles}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                        <input
                          type="password"
                          value={createUserForm.password}
                          onChange={(e) => setCreateUserForm({ ...createUserForm, password: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          required
                          disabled={creatingUser || !canManageRoles}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Confirm Password</label>
                        <input
                          type="password"
                          value={createUserForm.confirmPassword}
                          onChange={(e) => setCreateUserForm({ ...createUserForm, confirmPassword: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          required
                          disabled={creatingUser || !canManageRoles}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                        <select
                          value={createUserForm.role}
                          onChange={(e) => setCreateUserForm({ ...createUserForm, role: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          disabled={creatingUser || !canManageRoles}
                        >
                          <option value="super_admin">Super Admin</option>
                          <option value="admin">Admin</option>
                          <option value="accounts">Accounts</option>
                          <option value="deo">DEO</option>
                          <option value="instructor">Instructor (Live Classes)</option>
                        </select>
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <button
                        type="submit"
                        disabled={creatingUser || !canManageRoles}
                        className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${creatingUser ? 'bg-gray-400' : 'bg-primary-600 hover:bg-primary-700'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-60`}
                      >
                        {creatingUser ? 'Creating...' : 'Create User'}
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {adminUser?.role === 'super_admin' && (
        <div className="max-w-7xl mx-auto py-4 px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs text-gray-400">
            Made with <HeartIcon className="w-3 h-3 text-red-500 inline-block" /> by <span className="font-medium text-gray-500">Vishal Thakur</span>
          </p>
        </div>
      )}
    </div>
  );
};

export default AdminSettingsPage;